<script setup>
import { NFlex } from 'naive-ui';
import { computed } from 'vue';
import RadarChart from './RadarChart.vue';

import iconStudent from '@/assets/images/result/ai/icon_student.png';
import iconClass from '@/assets/images/result/ai/icon_class.png';
import iconSchool from '@/assets/images/result/ai/icon_school.png';

const props = defineProps({
  // 본인(학생용) 또는 우리 학급(학급용) 정답 비율
  myPercentages: {
    type: Array,
    default: () => []
  },
  // 학교 평균 정답 비율
  schoolPercentages: {
    type: Array,
    default: () => []
  },
  userRole: {
    type: String,
    default: 'teacher'
  },
  itemLabels: {
    type: Array,
    default: () => ['AI·디지털 기술', 'AI·디지털 활용', 'AI·디지털 윤리', '인간중심 AI 설계']
  }
});

const isTeacher = computed(() => props.userRole === 'teacher');

const formatRates = (values) => props.itemLabels.map((label, index) => {
  const raw = Number(values?.[index]);
  return {
    label,
    percent: Number.isFinite(raw) ? raw.toFixed(1) : 'N/A'
  };
});

// 본인(또는 우리 학급) / 학교 평균 두 묶음을 함께 보여준다
const groups = computed(() => ([
  {
    key: 'mine',
    label: isTeacher.value ? '우리 학급<br>정답 비율' : '나의 정답<br>비율',
    icon: isTeacher.value ? iconClass : iconStudent,
    rows: formatRates(props.myPercentages)
  },
  {
    key: 'school',
    label: '학교 평균<br>정답 비율',
    icon: iconSchool,
    rows: formatRates(props.schoolPercentages)
  }
]));
</script>

<template>
  <div class="card answer">
      <h5 class="card-title">영역별 정답 비율 비교</h5>
      <n-flex :wrap="false" :size="20" class="pt-2">
        <div class="chart-wrap" style="width: 550px">
          <div class="inner">
            <p class="label">{{ itemLabels[0] }}</p>
            <n-flex :wrap="false" :size="0">
              <p class="label">{{ itemLabels[3] }}</p>
              <RadarChart
                :item-labels="itemLabels"
                :our-class-percentages="myPercentages"
                :school-percentages="schoolPercentages"
              />
              <p class="label">{{ itemLabels[1] }}</p>
            </n-flex>
            <p class="label">{{ itemLabels[2] }}</p>
          </div>
        </div>

        <div class="box">
          <div
            v-for="group in groups"
            :key="group.key"
            class="rate-group"
            :class="group.key"
          >
            <div class="group-label">
              <img :src="group.icon" alt="">
              <span v-html="group.label"></span>
            </div>
            <ul class="rates">
              <li v-for="(item, index) in group.rows" :key="index">
                <span class="rate-label">{{ item.label }}</span>
                <span class="rate-colon">:</span>
                <span class="rate-percent">{{ item.percent }}%</span>
              </li>
            </ul>
          </div>

          <ul class="helper text-[1rem] flex flex-col pt-4">
            <li>
              <span class="ai-highlight font-bold">학교 평균 정답 비율</span>
              : 본 진단검사에 참여한 소속 학교의 해당 학년 학생 전체의 평균 정답 비율을 의미합니다.
            </li>
            <li>이 그래프는 하위 영역 간 상대적 수행 능력 차이를 의미하지 않습니다.</li>
          </ul>
        </div>
      </n-flex>
  </div>
</template>

<style lang="scss" scoped>
.box {
  background-color: var(--result-ai-primary-50);
  border-radius: 10px;
  flex: 1;
  padding: .75rem 1.25rem .875rem;

  .rate-group {
    display: flex;
    align-items: center;
    gap: 1rem;
    background-color: var(--smoe-white);
    border-radius: 0.625rem;
    padding: 0.8rem 1rem;
    & + .rate-group { margin-top: 0.75rem; }

    .group-label {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
      flex-shrink: 0;
      width: 6.5rem;
      padding: 0.5rem 0.25rem;
      border-radius: 0.5rem;
      font-size: 1rem;
      font-weight: 700;
      line-height: 1.3;
      text-align: center;
      img { width: 2rem; height: 2rem; }
    }

    &.mine .group-label {
      background-color: #FDEBF3;
      border: 1px solid #F3B6D1;
      color: #B23A6B;
    }
    &.school .group-label {
      background-color: #EAF4FE;
      border: 1px solid #A9CDF5;
      color: var(--result-ai-primary-800);
    }

    // 라벨 / 콜론 / 값을 ul 하나의 그리드로 묶어 줄마다 같은 위치에 오게 한다.
    // 칸 폭을 max-content로 두어 라벨 뒤에 빈 공간이 생기지 않는다.
    .rates {
      flex: 1;
      display: grid;
      // max-content(자동 계산)는 PDF 캡처(dom-to-image → SVG foreignObject) 과정에서
      // 화면과 다르게 좁게 계산되어 "인간중심 AI 설계"가 줄바꿈되는 문제가 있었다.
      // 자동 계산에 기대지 않도록 라벨 칸 너비를 고정값으로 못박는다.
      grid-template-columns: 9.5rem max-content max-content;
      justify-content: start;
      align-content: center;
      row-gap: 0.125rem;
      column-gap: 0.5rem;
      li { display: contents; }

      .rate-label {
        position: relative;
        padding-left: 0.75rem;
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.5;
        color: var(--result-ai-blak);
        white-space: nowrap;
        &::before {
          content: '';
          position: absolute;
          top: 0.7rem;
          left: 0;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: var(--result-ai-blak);
        }
      }
      .rate-colon {
        // 본문 기본 글자크기(19px)를 상속하면 줄 높이가 커져 행간이 벌어진다
        font-size: 1rem;
        line-height: 1.5;
        color: var(--result-ai-blak);
      }
      .rate-percent {
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.5;
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
    }
  }

  .helper {
    li {
      position: relative; padding-left: 24px; text-align: justify;
      &::before {
        content: '';
        position: absolute;
        top: 8px;
        left: 10px;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: var(--result-ai-primary-500);
      }
    }
  }
}
// 시안에 맞춰 그래프는 흰 박스(테두리) 없이 카드 위에 그대로 올린다
.chart-wrap {
  border: 0; padding: 20px 20px 20px 0;
  display: flex; align-items: center; justify-content: center;
  .inner {
    display: flex; flex-direction: column; align-items: center; font-weight: 600; position: relative;
    .label {
      font-size: 1rem;
      color: var(--result-ai-primary-800);
    }
  }
  .n-flex {
    gap: 0; align-items: center; text-align: center;
  }
}
</style>
